import React, { useState, useEffect } from 'react';
import AdminLayout from '../components/AdminLayout';
import AdminLink from '../components/AdminLink';
import { apiFetch } from '../jscontrol/apiClient';
import { pickRecordId } from '../jscontrol/recordUtils';
import '../css/Category.css';
import ProductAttributeValueCreate from './ProductAttributeValueCreate';
import ProductAttributeValueEdit from './ProductAttributeValueEdit';
import { isColorAttributeName, parseColorValueToCss } from '../../utils/colorUtils';

const ProductAttributeValuePage = ({ embedded = false, productId = null }) => {
  const [values, setValues] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [showAddModal, setShowAddModal] = useState(false);
  const [showEditModal, setShowEditModal] = useState(false);
  const [editItem, setEditItem] = useState(null);
  const [toast, setToast] = useState(null);

  const showToast = (message, type = 'danger') => {
    setToast({ message, type });
    setTimeout(() => setToast(null), 4000);
  };

  const [filterText, setFilterText] = useState('');
  const [sortKey, setSortKey] = useState('AttributeValue');
  const [sortDir, setSortDir] = useState('asc');
  const [page, setPage] = useState(1);
  const [PAGE_SIZE, setPageSize] = useState(10);
  const [totalCount, setTotalCount] = useState(0);

  const fetchValues = async (currentPage, pageSize, search, sk, sd) => {
    setLoading(true);
    setError(null);
    try {
      const sortColumn = sk === 'Status' ? 'IsActive' : sk;
      const params = new URLSearchParams({
        pageIndex: currentPage,
        pageSize: pageSize,
        search: search || '',
        sortColumn: sortColumn,
        sortBy: sd.toUpperCase(),
        ...(productId ? { productId } : {}),
      });
      const { data } = await apiFetch(`/ProductAttributeValue/GetAll?${params}`);
      const items = data?.data ?? [];
      const count = data?.totalRecords ?? data?.totalCount ?? (Array.isArray(items) ? items.length : 0);
      setValues(Array.isArray(items) ? items : []);
      setTotalCount(count);
    } catch (e) {
      setError(e.message);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchValues(page, PAGE_SIZE, filterText, sortKey, sortDir);
  }, [page, PAGE_SIZE, filterText, sortKey, sortDir, productId]);

  const openAdd = () => {
    setShowAddModal(true);
  };

  const openEdit = async (item) => {
    try {
      const { data } = await apiFetch(`/ProductAttributeValue/GetById/${item.ProductAttributeValueId}`);
      const fetched = data?.data ?? data ?? item;
      setEditItem(fetched);
    } catch (e) {
      setEditItem(item);
    }
    setShowEditModal(true);
  };

  const handleDelete = async (item) => {
    const id = pickRecordId(item, 'ProductAttributeValueId', 'productAttributeValueId');
    if (!id) {
      showToast('Could not delete — invalid value id.');
      return;
    }
    if (!window.confirm('Are you sure you want to delete this value?')) return;
    try {
      await apiFetch(`/ProductAttributeValue/Delete/${id}`, { method: 'DELETE' });
      showToast('Value deleted successfully.', 'success');
      fetchValues(page, PAGE_SIZE, filterText, sortKey, sortDir);
    } catch (e) {
      showToast(e.message);
    }
  };

  const totalPages = Math.max(1, Math.ceil(totalCount / PAGE_SIZE));
  const safePage = Math.min(page, totalPages);

  const goToPage = (p) => setPage(Math.max(1, Math.min(p, totalPages)));
  const handleFilter = (val) => { setFilterText(val); setPage(1); };

  const handleSortCol = (key) => {
    if (sortKey === key) {
      setSortDir(d => d === 'asc' ? 'desc' : 'asc');
    } else {
      setSortKey(key);
      setSortDir('asc');
    }
    setPage(1);
  };

  const handlePageSize = (val) => { setPageSize(Number(val)); setPage(1); };

  const SortIcon = ({ col }) => {
    if (sortKey !== col) return <i className="mdi mdi-sort ms-1" style={{ opacity: 0.3 }}></i>;
    return sortDir === 'asc'
      ? <i className="mdi mdi-sort-ascending ms-1" style={{ color: '#6f42c1' }}></i>
      : <i className="mdi mdi-sort-descending ms-1" style={{ color: '#6f42c1' }}></i>;
  };

  const innerContent = (
    <>
      {!embedded && (
        <p style={{ margin: '0 0 12px 0', fontSize: '0.85rem', color: '#6c757d' }}>
          <AdminLink to="/admin">Dashboard</AdminLink>
          <span style={{ margin: '0 6px' }}>/</span>
          <span>Product Attribute Value</span>
        </p>
      )}

      <div className={embedded ? '' : 'row'}>
        <div className={embedded ? '' : 'col-lg-12 grid-margin stretch-card'}>
          <div className={embedded ? '' : 'card'}>
            <div className={embedded ? '' : 'card-body'}>
              {/* Toast */}
              {toast && (
                <div
                  className={`alert alert-${toast.type} alert-dismissible d-flex align-items-center py-2 mb-3`}
                  role="alert"
                  style={{ fontSize: '0.875rem' }}
                >
                  <i className={`mdi ${toast.type === 'success' ? 'mdi-check-circle' : 'mdi-alert-circle'} me-2`} style={{ fontSize: '1.1rem' }}></i>
                  <span>{toast.message}</span>
                  <button type="button" className="btn-close ms-auto" style={{ fontSize: '0.7rem' }} onClick={() => setToast(null)} aria-label="Close" />
                </div>
              )}

              {/* Header */}
              <div className="d-flex justify-content-between align-items-center mb-3 flex-wrap gap-2">
                <h4 className="card-title mb-0">Product Attribute Value</h4>
                <div className="d-flex align-items-center gap-2">
                  <div className="cat-filter-wrap">
                    <i className="mdi mdi-magnify cat-filter-icon"></i>
                    <input
                      type="text"
                      className="cat-filter-input"
                      placeholder="Search value..."
                      value={filterText}
                      onChange={e => handleFilter(e.target.value)}
                    />
                  </div>
                  <button className="cat-add-btn" onClick={openAdd}>
                    <i className="mdi mdi-plus" style={{ marginRight: 6 }}></i> Add Value
                  </button>
                </div>
              </div>

              {loading ? (
                <div className="text-center py-5">
                  <div className="spinner-border text-primary" role="status">
                    <span className="visually-hidden">Loading...</span>
                  </div>
                </div>
              ) : error ? (
                <div className="alert alert-danger d-flex align-items-center py-2" style={{ fontSize: '0.875rem' }}>
                  <i className="mdi mdi-alert-circle me-2" style={{ fontSize: '1.1rem' }}></i>
                  {error}
                </div>
              ) : (
                <div className="table-responsive">
                  <table className="table table-striped">
                    <thead>
                      <tr>
                        <th className="cat-sort-th" onClick={() => handleSortCol('ProductAttributeName')} style={{ width: '35%' }}>
                          Attribute Name <SortIcon col="ProductAttributeName" />
                        </th>
                        <th className="cat-sort-th" onClick={() => handleSortCol('AttributeValue')} style={{ width: '50%' }}>
                          Value <SortIcon col="AttributeValue" />
                        </th>
                        <th style={{ width: '15%' }}>Actions</th>
                      </tr>
                    </thead>
                    <tbody>
                      {values.length === 0 ? (
                        <tr>
                          <td colSpan="3" className="text-center py-4 text-muted">
                            No attribute values found.
                          </td>
                        </tr>
                      ) : values.map((item) => (
                        <tr key={item.ProductAttributeValueId}>
                          <td>{item.ProductAttributeName || '—'}</td>
                          <td className="cat-name-cell">
                            <div className="d-flex align-items-center gap-2">
                              {isColorAttributeName(item.ProductAttributeName) &&
                              parseColorValueToCss(item.AttributeValue) ? (
                                <span
                                  style={{
                                    width: 22,
                                    height: 22,
                                    borderRadius: '50%',
                                    border: '1px solid #dee2e6',
                                    backgroundColor: parseColorValueToCss(item.AttributeValue),
                                    flexShrink: 0,
                                  }}
                                  title={item.AttributeValue}
                                />
                              ) : null}
                              <span>{item.AttributeValue}</span>
                            </div>
                          </td>
                          <td>
                            <button
                              className="btn btn-sm btn-outline-primary me-1"
                              style={{ border: 'none' }}
                              onClick={() => openEdit(item)}
                              title="Edit"
                            >
                              <i className="mdi mdi-pencil"></i>
                            </button>
                            <button
                              className="btn btn-sm btn-outline-danger"
                              style={{ border: 'none' }}
                              onClick={() => handleDelete(item)}
                              title="Delete"
                            >
                              <i className="mdi mdi-delete"></i>
                            </button>
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              )}

              {/* Pagination */}
              {!loading && !error && totalCount > 0 && (
                <div className="d-flex justify-content-between align-items-center mt-3 flex-wrap gap-2">
                  <div className="d-flex align-items-center gap-2">
                    <span className="cat-page-info">Rows per page:</span>
                    <select className="cat-pagesize-select" value={PAGE_SIZE} onChange={e => handlePageSize(e.target.value)}>
                      <option value={10}>10</option>
                      <option value={30}>30</option>
                      <option value={50}>50</option>
                    </select>
                    <span className="cat-page-info">
                      Showing {(safePage - 1) * PAGE_SIZE + 1}–{Math.min(safePage * PAGE_SIZE, totalCount)} of {totalCount}
                    </span>
                  </div>
                  <div className="cat-pagination">
                    <button className="cat-page-btn" onClick={() => goToPage(1)} disabled={safePage === 1} title="First">
                      <i className="mdi mdi-chevron-double-left"></i>
                    </button>
                    <button className="cat-page-btn" onClick={() => goToPage(safePage - 1)} disabled={safePage === 1} title="Previous">
                      <i className="mdi mdi-chevron-left"></i>
                    </button>
                    {Array.from({ length: totalPages }, (_, i) => i + 1)
                      .filter(p => p === 1 || p === totalPages || Math.abs(p - safePage) <= 1)
                      .reduce((acc, p, idx, arr) => {
                        if (idx > 0 && p - arr[idx - 1] > 1) acc.push('...');
                        acc.push(p);
                        return acc;
                      }, [])
                      .map((p, i) =>
                        p === '...' ? (
                          <span key={`e${i}`} className="cat-page-ellipsis">…</span>
                        ) : (
                          <button key={p} className={`cat-page-btn ${safePage === p ? 'active' : ''}`} onClick={() => goToPage(p)}>
                            {p}
                          </button>
                        )
                      )}
                    <button className="cat-page-btn" onClick={() => goToPage(safePage + 1)} disabled={safePage === totalPages} title="Next">
                      <i className="mdi mdi-chevron-right"></i>
                    </button>
                    <button className="cat-page-btn" onClick={() => goToPage(totalPages)} disabled={safePage === totalPages} title="Last">
                      <i className="mdi mdi-chevron-double-right"></i>
                    </button>
                  </div>
                </div>
              )}
            </div>
          </div>
        </div>
      </div>

      {showAddModal && (
        <ProductAttributeValueCreate
          onClose={() => setShowAddModal(false)}
          productId={productId}
          onSuccess={(msg) => {
            setShowAddModal(false);
            showToast(msg, 'success');
            fetchValues(page, PAGE_SIZE, filterText, sortKey, sortDir);
          }}
        />
      )}

      {showEditModal && editItem && (
        <ProductAttributeValueEdit
          editItem={editItem}
          onClose={() => { setShowEditModal(false); setEditItem(null); }}
          productId={productId}
          onSuccess={(msg) => {
            setShowEditModal(false);
            setEditItem(null);
            showToast(msg, 'success');
            fetchValues(page, PAGE_SIZE, filterText, sortKey, sortDir);
          }}
        />
      )}
    </>
  );

  return embedded ? innerContent : <AdminLayout>{innerContent}</AdminLayout>;
};

export default ProductAttributeValuePage;
